/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { format, parseISO } from 'date-fns';
import moment from 'moment-timezone';

import ModalFull from '@components/modal/ModalFull';
import SociollaText from '@components/sociolla/SociollaText';
import ItemDetail from '@components/global/ItemDetail';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import Button from '@components/form/Button';
import RenderIf from '@components/global/RenderIf';
import * as Layout from '@components/screen/Layout';

import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsAccount } from '@api/ms-account';

import Colors from '@global/colors';
import { isWeb, fullHeight, isMobileWeb } from '@global/screens';
import PropTypes from 'prop-types';

const image = require('@/assets/img/default/user-image.png');

const propTypes = {
  testID: PropTypes.string,
  status: PropTypes.string,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  logType: PropTypes.string,
  logId: PropTypes.string,
  logLabel: PropTypes.string,
  logFormatter: PropTypes.object,
  data: PropTypes.object,
};

const DEFAULT_LIMIT = 10;

// formatLogs() returns either a plain string (diff/custom-formatted lines, which
// are data and don't need translation) or a { field, action } object for
// add/remove entries. The field name is data, but "added"/"removed" is a UI
// string — rendering it as its own SociollaText child (rather than baking it
// into one interpolated string) lets it actually go through translate().
const LogLine = ({ log, style }) =>
  typeof log === 'string' ? (
    <SociollaText style={style}>- {log}</SociollaText>
  ) : (
    <SociollaText style={style}>
      - [{log.field}] {log.action}
    </SociollaText>
  );

LogLine.propTypes = {
  log: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      field: PropTypes.string,
      action: PropTypes.string,
    }),
  ]).isRequired,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

const ModalLog = (props) => {
  const [data, setData] = useState([]);
  const [tempData, setTempData] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const getLpbLogs = useApi(MsOrder.getLpbLogs);
  const getSkpExternalLogs = useApi(MsOrder.getSkpExternalLogs);
  const getSkpInternalLogs = useApi(MsOrder.getSkpInternalLogs);
  const getCustomerLog = useApi(MsAccount.getCustomerLogs);
  const getProductListingLog = useApi(MsOrder.getProductListingLog);
  const getRequestProductTesterLogs = useApi(
    MsOrder.getRequestProductTesterLogs,
  );
  const getUserLog = useApi(MsAccount.getUserLogs);

  const isValidISODateMoment = (value) => {
    if (typeof value !== 'string') {
      return false;
    }
    const isoDatePattern =
      /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;

    if (!isoDatePattern.test(value)) {
      return false;
    }

    return moment(value, moment.ISO_8601, true).isValid();
  };

  const formatValue = (val) => {
    const isValidDate = isValidISODateMoment(val);

    if (isValidDate) {
      return moment(val).format('DD MMM YYYY');
    }

    return val;
  };

  const formatLogs = (params) => {
    const logChangeType = props.logFormatter[params.change_type];

    if (params.change_type == 'update') {
      return params.logs.map((l) => {
        const fieldLabel = logChangeType[l.field_name] || l.field_name;

        if (typeof fieldLabel == 'function') {
          return fieldLabel({ log: l, params, doc: props.data });
        }
        if (l.change_type == 'add') {
          return { field: fieldLabel, action: 'added' };
        }
        if (l.change_type == 'remove') {
          return { field: fieldLabel, action: 'removed' };
        }
        const fromValue = formatValue(l.from_value);
        const toValue = formatValue(l.to_value);
        return `[${fieldLabel}]: ${fromValue} -> ${toValue}`;
      });
    }

    if (logChangeType && typeof logChangeType == 'function') {
      return logChangeType({ params, doc: props.data });
    }
    return [{ field: props.logLabel, action: `${params.change_type}d` }];
  };

  const generateData = async (newSkip = 0) => {
    try {
      let resLog;
      if (props.logType == 'lpb') {
        resLog = await getLpbLogs.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'skp_external') {
        resLog = await getSkpExternalLogs.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'customer') {
        resLog = await getCustomerLog.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'skp_internal') {
        resLog = await getSkpInternalLogs.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'product_listing') {
        resLog = await getProductListingLog.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'user') {
        resLog = await getUserLog.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else if (props.logType == 'request_product_tester') {
        resLog = await getRequestProductTesterLogs.request(props.logId, {
          limit: DEFAULT_LIMIT,
          skip: newSkip,
        });
      } else {
        console.error('Log Type not provided!');
        return;
      }
      setTempData(resLog?.data?.data || []);
      if (resLog?.data?.data && Object.keys(resLog.data.data).length) {
        const list = resLog.data.data.map((x) => {
          return {
            ...x,
            datetime: format(parseISO(x.created_at), 'dd MMM yyyy | hh.mm a'),
            name: x.created_by?.name,
            image: x.created_by?.image,
            logs: formatLogs(x),
          };
        });
        if (newSkip) {
          setData((prevState) => prevState.concat(list));
        } else {
          setData(list);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setLoading(true);
      generateData();
    }
    return () => (mounted = false);
  }, [props]);

  const ModalContent = () => {
    return (
      <View style={{ paddingVertical: 20 }}>
        <ItemDetail
          testID={`${props.testID}-status`}
          name="Status"
          style={{ paddingHorizontal: 20 }}
          nameStyle={{ alignSelf: 'center' }}
          valueComponent={<StatusBadge status={props.status} />}
          value="-"
        />
        {data.length > 0 ? (
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            {data.map((his, index) => {
              return isWeb ? (
                <View
                  testID={`${props.testID}-log-${index}`}
                  key={'list-log-' + index}
                  style={[styles.logItem, { paddingTop: index > 0 ? 0 : 20 }]}>
                  <View style={styles.logIdentity}>
                    <SociollaText
                      style={{
                        flex: 1,
                        fontSize: 14,
                        color: Colors.black1,
                        opacity: 0.35,
                      }}>
                      {his.datetime}
                    </SociollaText>
                    <View style={styles.logUser}>
                      <Image
                        style={{ width: 20, height: 20, marginRight: 8 }}
                        width={20}
                        height={20}
                        isResolveAsset
                        source={his.image ? { uri: his.image } : image}
                      />
                      <SociollaText
                        style={{
                          fontSize: 12,
                          color: Colors.blue3,
                          alignSelf: 'center',
                        }}>
                        {his.name}
                      </SociollaText>
                    </View>
                  </View>
                  {(his.logs || []).map((l, i) => (
                    <LogLine
                      key={i}
                      log={l}
                      style={{
                        flex: 1,
                        fontSize: 14,
                        color: Colors.black1,
                        marginBottom: 3,
                      }}
                    />
                  ))}
                </View>
              ) : (
                <View
                  testID={`${props.testID}-log-${index}`}
                  key={'list-log-' + index}
                  style={{
                    padding: 20,
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: Colors.grey0,
                  }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: 10,
                    }}>
                    <SociollaText
                      style={{
                        fontSize: 14,
                        color: Colors.black1,
                        opacity: 0.35,
                      }}>
                      {his.datetime}
                    </SociollaText>
                    <View
                      style={{
                        flex: 1,
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                      }}>
                      <Image
                        style={{ width: 20, height: 20, marginRight: 8 }}
                        width={20}
                        height={20}
                        isResolveAsset
                        source={his.image ? { uri: his.image } : image}
                      />
                      <SociollaText
                        style={{
                          fontSize: 12,
                          color: Colors.blue3,
                          alignSelf: 'center',
                        }}
                        numberOfLines={1}>
                        {his.name}
                      </SociollaText>
                    </View>
                  </View>
                  {(his.logs || []).map((l, i) => (
                    <LogLine
                      key={i}
                      log={l}
                      style={{
                        flex: 1,
                        fontSize: 14,
                        color: Colors.black1,
                        marginBottom: 3,
                      }}
                    />
                  ))}
                </View>
              );
            })}
            {tempData.length >= DEFAULT_LIMIT && (
              <View style={styles.loadMoreContainer}>
                <Button
                  testID={`${props.testID}-load-more-btn`}
                  type="primary"
                  rounded
                  buttonStyle={{
                    width: 185,
                    marginRight: 10,
                    backgroundColor: Colors.blue0,
                  }}
                  title={
                    <SociollaText type="bold" style={{ color: Colors.white }}>
                      Load More
                    </SociollaText>
                  }
                  onPress={() => {
                    const newSkip = skip + DEFAULT_LIMIT;
                    setSkip(newSkip);
                    generateData(newSkip);
                  }}
                />
              </View>
            )}
          </ScrollView>
        ) : (
          <SociollaText style={{ textAlign: 'center', paddingVertical: 40 }}>
            No log found.
          </SociollaText>
        )}
      </View>
    );
  };

  return (
    <ModalFull
      testID={props.testID}
      show={props.show}
      title="Log"
      width={isWeb ? 650 : '100%'}
      onClose={props.onClose}>
      <RenderIf condition={loading}>
        <Layout.Loader />
      </RenderIf>
      <RenderIf condition={!loading}>
        <ModalContent />
      </RenderIf>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    paddingBottom: 200,
    maxHeight: isWeb ? 400 : isMobileWeb ? fullHeight - 160 : 'none',
  },
  logItem: {
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-around',
    paddingHorizontal: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    marginBottom: 20,
  },
  logIdentity: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 10,
  },
  logUser: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginRight: 20,
  },
  loadMoreContainer: {
    alignItems: 'center',
    marginBottom: 100,
  },
});

ModalLog.propTypes = propTypes;

export default ModalLog;
